import { useState } from "react";
import { Link } from "react-router-dom";
import { Quote, Camera } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import Lightbox from "@/components/Lightbox";
import SkylineDivider from "@/components/SkylineDivider";
import { IMAGES, NOUWEN_QUOTE } from "@/lib/images";

const GALLERY_IMAGES = [
  IMAGES.imgGal01, IMAGES.imgGal02, IMAGES.imgGal03, IMAGES.imgGal04,
  IMAGES.imgGal05, IMAGES.imgGal06, IMAGES.imgGal07, IMAGES.imgGal08,
  IMAGES.imgGal09, IMAGES.imgGal10, IMAGES.imgGal11, IMAGES.imgGal12,
  IMAGES.imgGal13, IMAGES.imgPondFeature, IMAGES.imgPropertyOverview, IMAGES.hilltopSunset,
  IMAGES.driveView01, IMAGES.driveView02, IMAGES.driveView03, IMAGES.driveView04,
  IMAGES.driveView05, IMAGES.newView06, IMAGES.newView10, IMAGES.mlsMistyHills,
  IMAGES.mlsGoldenHourSnow, IMAGES.mlsPondLilies, IMAGES.mainHouseExterior,
];

export default function Farm() {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  return (
    <div data-testid="farm-page">
      <section className="container-x pt-12 lg:pt-24 pb-16" data-testid="farm-hero">
        <FadeIn><p className="overline mb-5">The Farm</p></FadeIn>
        <FadeIn delay={120}>
          <h1 className="font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.02] tracking-tight max-w-4xl">
            50 acres in the shadow of <em className="not-italic text-clay">Hawk Mountain</em>.
          </h1>
        </FadeIn>
        <FadeIn delay={240}>
          <p className="mt-8 max-w-2xl text-lg text-coal/70 leading-[1.85]">
            Iron Talon Farm sits in Kempton, Pennsylvania, where the Blue Ridge folds into
            hardwood forest and open pasture. A main house from the mid 1800s, six historic cottages
            thoughtfully restored, gardens, a pond, and the quiet of a working farm.
          </p>
        </FadeIn>
      </section>

      <FadeIn className="container-x">
        <img src={IMAGES.imgSkylineSunsetField} alt="Ridge line silhouette at sunset — the mountain view from Iron Talon Farm" className="w-full aspect-[3/2] lg:aspect-[21/9] object-cover object-bottom" />
      </FadeIn>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="farm-skyline-divider-top" />

      {/* HISTORY — moved above Stewards per owner request (Feb 2026) */}
      <section className="container-x py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20" data-testid="farm-history">
        <FadeIn className="lg:col-span-5">
          <p className="overline mb-5">History</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight">
            Built in the <em className="not-italic text-clay">mid 1800s</em>. Tended ever since.
          </h2>
        </FadeIn>
        <FadeIn delay={120} className="lg:col-span-7 text-coal/75 text-lg leading-[1.9] space-y-5">
          <p>
            The main house has stood through more than a century and a half of Pennsylvania winters. Hand-laid stone
            foundations, hemlock beams, and the kind of quiet that only old buildings carry.
          </p>
          <p>
            Around it, the other dwellings — once a milk house, a grain shed, a hay loft, a summer
            kitchen — have been carefully renovated to honor their roots. The work has been thoughtful,
            ecological, and respectful of the rich Pennsylvania Dutch heritage that shaped this land.
          </p>
          <p>
            We've restored what needed restoring and left the rest as it was — the marks of work,
            weather, and two centuries of quiet mornings.
          </p>
        </FadeIn>
      </section>

      <SkylineDivider variant="moss" height={90} opacity={0.5} className="bg-cream" data-testid="farm-skyline-divider" />

      {/* THE STEWARDS — Dan & Catherine */}
      <section className="container-x py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20" data-testid="farm-stewards">
        <FadeIn className="lg:col-span-5">
          <p className="overline mb-5">The Stewards</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight">
            <em className="not-italic text-clay">Dan, Catherine, and Bunny</em>.
          </h2>
        </FadeIn>
        <FadeIn delay={120} className="lg:col-span-7 text-coal/75 text-lg leading-[1.9] space-y-5">
          <p>
            Iron Talon is stewarded by Dan and Catherine — and by Bunny, our
            resident farm dog, who often greets guests at the gravel. (For the
            comfort of all guests, outside pets are not permitted.)
          </p>
          <p>
            Dan is in his 25th year of law enforcement. Catherine has a background connected to
            addiction recovery and is currently pursuing her master's degree in clinical mental
            health counseling.
          </p>
          <p>
            Together, while daily asking God to guide their steps, they built this place to be
            the kind of space they once needed themselves — quiet, steady, private, and rooted in
            hope. A place to breathe, recover, reconnect, and return stronger.
          </p>
          <p>
            The work of The Shadow Foundation and New Leaf Wellness Collective grew from that
            same purpose: to hold space for others walking difficult roads and to offer a place
            where healing can begin.
          </p>
          <p className="italic text-clay">
            "The men who answer the call need ground of their own — a place to stand down,
            gather strength, and return ready."
          </p>
        </FadeIn>
      </section>

      {/* THE THREE NAMES — scripture meanings */}
      <section className="container-x py-24 lg:py-32" data-testid="farm-three-names">
        <FadeIn>
          <p className="overline mb-5">The Meaning Behind the Names</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight max-w-2xl">
            Every name here <em className="not-italic text-clay">carries a verse</em>.
          </h2>
          <p className="mt-6 max-w-2xl text-coal/70 text-lg leading-[1.85]">
            These names weren't chosen lightly. Each one is rooted in scripture we hold close —
            and in the place itself, here on the edge of Hawk Mountain in Kempton, Pennsylvania.
          </p>
        </FadeIn>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {[
            {
              name: "Iron Talon Farm",
              verse: "“As iron sharpens iron, so one person sharpens another.”",
              citation: "Proverbs 27:17",
              desc: "Named for the verse and for the land — we sit near Hawk Mountain, where raptors ride the ridge each fall. Iron talons, iron sharpening iron. Strength held with purpose.",
            },
            {
              name: "The Shadow Foundation",
              verse: "“Keep me as the apple of your eye; hide me in the shadow of your wings.”",
              citation: "Psalm 17:8",
              desc: "A place of refuge — the way scripture describes resting in the shadow of God's wings. Quiet, hidden, safe. For those who carry heavy burdens for others, this is shelter.",
            },
            {
              name: "New Leaf Wellness",
              verse: "“I am the vine; you are the branches… he prunes so that we may bear much fruit.”",
              citation: "John 15:1–5",
              desc: "A new leaf — the kind that grows after gentle pruning. Healing, growth, and the slow work of becoming who we were always meant to be.",
            },
          ].map((n, i) => (
            <FadeIn key={n.name} delay={i * 140}>
              <div className="border-t border-moss/20 pt-8 h-full">
                <p className="font-serif italic text-xl text-clay leading-[1.5]">{n.verse}</p>
                <p className="mt-3 overline">— {n.citation}</p>
                <h3 className="mt-8 font-serif text-2xl text-moss font-medium">{n.name}</h3>
                <p className="mt-3 text-coal/70 leading-[1.85]">{n.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* NOUWEN QUOTE */}
      <section className="bg-khaki py-24 lg:py-32" data-testid="farm-quote">
        <div className="container-narrow text-center">
          <FadeIn>
            <Quote strokeWidth={1.2} size={32} className="mx-auto text-clay" />
          </FadeIn>
          <FadeIn delay={120}>
            <p className="mt-8 font-serif italic text-3xl lg:text-5xl text-moss font-light leading-[1.25] tracking-tight">
              "{NOUWEN_QUOTE.quote}"
            </p>
          </FadeIn>
          {NOUWEN_QUOTE.author && (
            <FadeIn delay={260}>
              <p className="mt-10 overline">— {NOUWEN_QUOTE.author}</p>
            </FadeIn>
          )}
        </div>
      </section>

      {/* LIFE ON THE FARM */}
      <section id="gallery" className="container-x py-24 lg:py-32" data-testid="farm-life">
        <FadeIn>
          <p className="overline mb-5">Life Here</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 max-w-6xl">
            <h2 className="font-serif text-4xl lg:text-5xl text-moss font-light leading-tight tracking-tight lg:max-w-2xl">
              A genuine working farm, <em className="not-italic text-clay">thoughtfully shared</em> with guests.
            </h2>
            <Link
              to="/gallery"
              className="group inline-flex items-center gap-3 self-start lg:self-end px-6 py-3.5 bg-moss text-cream hover:bg-clay transition-colors duration-500 tracking-[0.2em] uppercase text-xs font-medium flex-shrink-0"
              data-testid="farm-launch-gallery-btn"
            >
              <Camera size={16} strokeWidth={1.5} />
              Walk the Land — Open Gallery
              <span className="ml-1 opacity-70 group-hover:translate-x-0.5 transition-transform">→</span>
            </Link>
          </div>
        </FadeIn>

        <div className="mt-16 grid grid-cols-12 grid-rows-2 gap-3 lg:gap-4 h-auto lg:h-[640px]">
          <Link to="/gallery" className="col-span-12 lg:col-span-7 row-span-2 overflow-hidden group">
            <img src={IMAGES.heroSunset3} alt="Sunset over the rolling hills" className="w-full h-full aspect-[4/3] lg:aspect-auto object-cover transition-transform duration-1000 group-hover:scale-105" />
          </Link>
          <Link to="/gallery" className="col-span-6 lg:col-span-5 overflow-hidden group">
            <img src={IMAGES.poppy} alt="Garden poppy" className="w-full h-full aspect-[4/3] lg:aspect-auto object-cover transition-transform duration-1000 group-hover:scale-105" />
          </Link>
          <Link to="/gallery" className="col-span-6 lg:col-span-5 overflow-hidden group">
            <img src={IMAGES.pondCattails} alt="Pond with cattails" className="w-full h-full aspect-[4/3] lg:aspect-auto object-cover transition-transform duration-1000 group-hover:scale-105" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow py-28 lg:py-36 text-center" data-testid="farm-cta">
        <FadeIn>
          <h2 className="font-serif text-4xl lg:text-6xl text-moss font-light leading-[1.05] tracking-tight">
            Come as you are.
            <br />
            <em className="not-italic text-clay">No judgment. Just peace and purpose.</em>
          </h2>
        </FadeIn>
        <FadeIn delay={140}>
          <p className="mt-8 text-lg text-coal/70 leading-[1.85] max-w-xl mx-auto">
            Whether you're looking for a quiet stay, a place to gather, or respite as someone
            who carries heavy burdens — there is space here for you. No performance required.
          </p>
        </FadeIn>
        <FadeIn delay={280}>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/stay" className="btn-primary" data-testid="farm-cta-stay-btn">Plan a Stay</Link>
            <Link to="/visit" className="btn-secondary" data-testid="farm-cta-contact-btn">Get in touch</Link>
          </div>
        </FadeIn>
      </section>

      <Lightbox
        open={galleryOpen}
        images={GALLERY_IMAGES}
        index={galleryIndex}
        title="Walk the Land"
        onClose={() => setGalleryOpen(false)}
        onIndexChange={setGalleryIndex}
      />
    </div>
  );
}
